import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, Filters, EmptyState, Button, Spinner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { EditIcon } from "@shopify/polaris-icons";
import { RootState, AppDispatch } from "configureStore";
import {
  fetchDepartmentsThunk,
  fetchMoreDepartments,
  setFilterValue,
} from "containers/departments/departments-container.slice";
import DataTable from "components/datatable/Datatable";
import HeadDataAccessBadge from "./head-data-access-badge";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { Column } from "types/Datatable";
import useIsPhone from "features/files/use-is-phone";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import {
  AvatarPile,
  MemberAvatar,
  departmentColor,
} from "containers/departments/department-visuals";

const END_REACHED_THRESHOLD = 200;
const EMPTY_STATE_IMAGE =
  "https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png";

/**
 * Departments list panel (inside the Departments tab). Server-paginated
 * infinite scroll with a search filter and skeleton loading — mirrors the
 * business-trips list pattern, minus its own Page wrapper. Without
 * `canManage` there is no edit pencil.
 */
function DepartmentsList({ canManage }: { canManage: boolean }) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams();
  const navigate = useNavigate();
  const isPhone = useIsPhone();

  const {
    collection,
    filtersValue,
    isLoading,
    isLoadingMore,
    isError,
    pagination,
  } = useSelector((state: RootState) => state.departmentsReducer);

  const headerRef = useRef<HTMLDivElement>(null);
  const listWrapRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [listMaxHeight, setListMaxHeight] = useState("calc(100vh - 320px)");
  const [queryInput, setQueryInput] = useState(filtersValue.textSearch || "");

  const hasNextPage = Boolean(pagination?.hasNextPage);
  const nextPage = (pagination?.page || 1) + 1;

  // Debounce the search box into the store (which triggers a page-1 reload).
  useEffect(() => {
    const handle = setTimeout(() => {
      if ((filtersValue.textSearch || "") !== queryInput) {
        dispatch(setFilterValue({ key: "textSearch", value: queryInput }));
      }
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryInput]);

  // Initial load + reload page 1 whenever company or filters change.
  useEffect(() => {
    if (companyId) dispatch(fetchDepartmentsThunk(companyId, filtersValue));
  }, [dispatch, filtersValue, companyId]);

  // Cap the table body height so only the rows scroll (uses DataTable's
  // existing maxBodyHeight — no component change).
  useLayoutEffect(() => {
    const compute = () => {
      const el = listWrapRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const available = window.innerHeight - top - 24;
      setListMaxHeight(`${Math.max(Math.round(available), 240)}px`);
    };
    compute();
    window.addEventListener("resize", compute);
    let resizeObserver: ResizeObserver | undefined;
    if (headerRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(compute);
      resizeObserver.observe(headerRef.current);
    }
    return () => {
      window.removeEventListener("resize", compute);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  const loadMore = () => {
    if (companyId && hasNextPage && !isLoading && !isLoadingMore) {
      dispatch(fetchMoreDepartments(companyId, filtersValue, nextPage));
    }
  };

  // Table infinite scroll (tablet, computer): listen to the DataTable's
  // internal scroll area.
  useEffect(() => {
    if (isPhone) return undefined;
    const wrap = listWrapRef.current;
    if (!wrap) return undefined;
    const scrollEl = wrap.querySelector<HTMLElement>(".overflow-y-auto");
    if (!scrollEl) return undefined;

    const onScroll = () => {
      if (isLoading || isLoadingMore || !hasNextPage) return;
      if (
        scrollEl.scrollTop + scrollEl.clientHeight >=
        scrollEl.scrollHeight - END_REACHED_THRESHOLD
      ) {
        loadMore();
      }
    };

    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // auto-fill if the first page doesn't fill the viewport
    return () => scrollEl.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    filtersValue,
    hasNextPage,
    isLoading,
    isLoadingMore,
    collection.length,
    isPhone,
  ]);

  // Phone infinite scroll: cards flow in the page, observe a sentinel.
  useEffect(() => {
    if (!isPhone) return undefined;
    const target = sentinelRef.current;
    if (!target) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "200px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, filtersValue, hasNextPage, isLoading, isLoadingMore, isPhone]);

  // Row click opens the department's detail page.
  const onRowClick = ({ id }: { id: string }) => {
    navigate(`/app/${companyId}/departments/${id}`);
  };

  // Edit pencil opens the create/edit modal (does not navigate).
  const openEditModal = (id: string) => {
    dispatch(setModalxComponent("DepartmentForm"));
    dispatch(setModalxComponentProps({ id }));
  };

  const handleQueryChange = (value: string) => setQueryInput(value);
  const handleQueryClear = () => {
    setQueryInput("");
    dispatch(setFilterValue({ key: "textSearch", value: "" }));
  };

  const appliedFilters: any[] = [];
  if (filtersValue.textSearch) {
    appliedFilters.push({
      key: "textSearch",
      label: `${t("search")}: ${filtersValue.textSearch}`,
      onRemove: handleQueryClear,
    });
  }

  const rows = (collection || []).map((department, idx) => ({
    id: department.id,
    "#": idx + 1,
    name: department.name,
    head: department.head,
    headHasDataAccess: !!department.head && !!department.headHasDataAccess,
    members: department.members || [],
    memberCount: department.memberCount,
  }));

  // Shared hover/link styling for the clickable text inside custom cells,
  // matching the DataTable's default link treatment. On a phone the card text
  // is the tap target, so it gets a full finger-sized height.
  const linkClass =
    "text-[#3F3227] transition-colors hover:text-[#B54708] hover:underline max-md:min-h-11 max-md:text-left";

  const columns = [
    { key: "#" },
    {
      key: "name",
      name: t("name"),
      type: "custom",
      mobileView: "topLeft",
      render: (item: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            className="dept-dot"
            style={{ background: departmentColor(item.id || item.name) }}
          />
          <button
            type="button"
            className={linkClass}
            onClick={() => onRowClick(item)}
          >
            {item.name}
          </button>
        </div>
      ),
    },
    {
      key: "head",
      name: t("head"),
      type: "custom",
      mobileView: "topRight",
      render: (item: any) =>
        item.head ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
              minWidth: 0,
            }}
          >
            <MemberAvatar member={item.head} size={24} />
            <button
              type="button"
              className={linkClass}
              onClick={() => onRowClick(item)}
            >
              {item.head.name}
            </button>
            {item.headHasDataAccess && <HeadDataAccessBadge />}
          </div>
        ) : (
          <span style={{ color: "#8a8f98" }}>—</span>
        ),
    },
    {
      key: "members",
      name: t("members"),
      type: "custom",
      mobileView: "bottomLeft",
      render: (item: any) => (
        <div
          className="max-md:min-h-11"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            cursor: "pointer",
          }}
          onClick={() => onRowClick(item)}
        >
          <AvatarPile members={item.members} />
          <span style={{ color: "#6b7280", fontSize: 13 }}>
            {item.memberCount}
          </span>
        </div>
      ),
    },
    ...(canManage
      ? [
          {
            key: "edit",
            name: "",
            type: "actionBtn",
            Icon: EditIcon,
            onClick: (item: any) => openEditModal(item.id),
            mobileView: "bottomRight",
          },
        ]
      : []),
  ] as Column[];

  const showSkeleton = isLoading && rows.length === 0;
  const showEmpty = !isLoading && !isError && rows.length === 0;

  return (
    <Card padding="0">
      <div ref={headerRef} style={{ padding: 12 }}>
        <Filters
          queryValue={queryInput}
          queryPlaceholder={t("search")}
          filters={[]}
          appliedFilters={appliedFilters}
          onQueryChange={handleQueryChange}
          onQueryClear={handleQueryClear}
          onClearAll={handleQueryClear}
          hideFilters
        />
      </div>
      <div ref={listWrapRef}>
        {showSkeleton ? (
          <DataTableSkeleton rows={8} columns={columns.length} />
        ) : isError ? (
          <EmptyState heading={t("fetch_error")} image={EMPTY_STATE_IMAGE}>
            <Button
              onClick={() =>
                companyId &&
                dispatch(fetchDepartmentsThunk(companyId, filtersValue))
              }
            >
              {t("retry", { defaultValue: "Retry" })}
            </Button>
          </EmptyState>
        ) : showEmpty ? (
          <EmptyState heading={t("no_departments")} image={EMPTY_STATE_IMAGE} />
        ) : (
          <>
            <DataTable
              collection={rows}
              columns={columns}
              maxBodyHeight={isPhone ? undefined : listMaxHeight}
            />
            <div ref={sentinelRef} style={{ height: 1 }} />
            {isLoadingMore && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 12,
                }}
              >
                <Spinner
                  accessibilityLabel={t("loading", { defaultValue: "Loading" })}
                  size="small"
                />
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

export default DepartmentsList;
